import React, { useState, useEffect } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { Button, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import {
	getTaxNumberLabel,
	getTaxNumberPlaceholder,
} from "../PharmacyManageTax/taxConfigUtils";
import { createSupplierAPI, updateSupplierAPI } from "./SupplierService";
import { useCountryStateSelect } from "../../Hooks/useCountryStateSelect";
import CountrySelect from "../Common/CountrySelect";
import StateSelect from "../Common/StateSelect";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";

const EMPTY_SUPPLIER = {
	companyName: "",
	name: "",
	email: "",
	phone: "",
	phoneCode: "",
	address: "",
	country: "",
	state: "",
	stateCode: "",
	district: "",
	city: "",
	pincode: "",
	gstNumber: "",
	panNumber: "",
};

const SupplierModal = ({ show, onHide, clinicId, onSaved, initial = null, isEdit = false }) => {
	const { t } = useTranslation();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const {
		country,
		state,
		states,
		statesLoading,
		showStateCode,
		onCountryChange,
		onStateChange,
		hydrate,
		reset: resetLocation,
	} = useCountryStateSelect({ enabled: !!show });

	const taxNumberLabel = getTaxNumberLabel(pharmacyPOS, t, country);
	const taxNumberPlaceholder = getTaxNumberPlaceholder(pharmacyPOS, t, country);

	const [currentSupplier, setCurrentSupplier] = useState(EMPTY_SUPPLIER);
	const [formErrors, setFormErrors] = useState({});

	useEffect(() => {
		if (!show) return;

		if (initial) {
			setCurrentSupplier({
				companyName: initial.companyName || "",
				name: initial.contactPerson || initial.name || "",
				email: initial.email || "",
				phone: initial.phone || "",
				phoneCode: initial.countryCode || initial.phoneCode || "",
				address: initial.address || "",
				country: initial.country || "",
				state: initial.state || "",
				stateCode: initial.stateCode || "",
				district: initial.district || "",
				city: initial.city || "",
				pincode: initial.pincode || "",
				gstNumber: initial.gstNumber || "",
				panNumber: initial.panNumber || "",
			});
			hydrate({
				country: initial.country || "",
				state: initial.state || "",
				stateCode: initial.stateCode || "",
			});
			setFormErrors({});
			return;
		}

		setCurrentSupplier(EMPTY_SUPPLIER);
		resetLocation();
		setFormErrors({});
	}, [show, initial, hydrate, resetLocation]);

	const handleCountryChange = (option) => {
		onCountryChange(option);
		setCurrentSupplier((prev) => ({
			...prev,
			country: option?.label || "",
			state: "",
			stateCode: "",
		}));
	};

	const handleStateChange = (option) => {
		onStateChange(option);
		setCurrentSupplier((prev) => ({
			...prev,
			state: option?.label || "",
			stateCode: showStateCode ? option?.gstCode || "" : "",
		}));
	};

	const validateSupplierForm = () => {
		const errors = {};
		if (!currentSupplier.companyName?.trim()) {
			errors.companyName = t("Company Name is required");
		}
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSaveSupplier = async () => {
		if (!validateSupplierForm()) return;

		const payload = {
			clinicId,
			companyName: currentSupplier.companyName,
			contactPerson: currentSupplier.name,
			email: currentSupplier.email,
			countryCode: currentSupplier.phoneCode,
			phone: currentSupplier.phone,
			address: currentSupplier.address,
			country: currentSupplier.country,
			state: currentSupplier.state,
			stateCode: showStateCode ? currentSupplier.stateCode || "" : "",
			district: currentSupplier.district,
			city: currentSupplier.city,
			pincode: currentSupplier.pincode,
			gstNumber: currentSupplier.gstNumber || "",
			panNumber: currentSupplier.panNumber || "",
		};

		try {
			setLoading(true);
			if (isEdit && initial?._id) {
				payload.supplierId = initial._id;
				const response = await updateSupplierAPI(payload);
				if (response.data.status === "success") {
					toast.success(t(response.data.message));
					const updated = response.data?.data?.supplier || response.data?.data || null;
					onSaved?.(updated);
					handleOnHide();
				} else {
					toast.warning(response?.data?.message || "Failed to update supplier");
				}
			} else {
				const response = await createSupplierAPI(payload);
				if (response?.data?.status === "success") {
					toast.success(t(response.data.message || "Supplier created"));
					const created = response.data?.data?.supplier || response.data?.data || null;
					onSaved?.(created);
					handleOnHide();
				} else {
					toast.warning(response?.data?.message || "Failed to create supplier");
				}
			}
		} catch (err) {
			console.error("Create supplier error", err);
			toast.warning(t("Operation failed"));
		} finally {
			setLoading(false);
		}
	};

	const handleOnHide = () => {
		setCurrentSupplier(EMPTY_SUPPLIER);
		resetLocation();
		setFormErrors({});
		onHide();
	};

	return (
		<Modal show={!!show} onHide={handleOnHide} size="lg" centered>
			<Modal.Header closeButton>
				<Modal.Title>{initial ? t("Edit Supplier") : t("Add Supplier")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Row>
					<Col md={6}>
						<div className="mb-3">
							<label className="form-label">
								{t("Company Name")} <span className="text-danger">*</span>
							</label>
							<Input
								type="text"
								value={currentSupplier.companyName}
								onChange={(e) =>
									setCurrentSupplier((prev) => ({
										...prev,
										companyName: e.target.value,
									}))
								}
								invalid={!!formErrors.companyName}
								placeholder={t("Enter company name")}
							/>
							{formErrors.companyName && (
								<div className="text-danger mt-1 small">{formErrors.companyName}</div>
							)}
						</div>
					</Col>
					<Col md={6}>
						<div className="mb-3">
							<label className="form-label">{t("Contact Person")}</label>
							<Input
								type="text"
								value={currentSupplier.name}
								onChange={(e) =>
									setCurrentSupplier((prev) => ({ ...prev, name: e.target.value }))
								}
								placeholder={t("Enter contact person name")}
							/>
						</div>
					</Col>
				</Row>

				<Row>
					<Col md={6}>
						<div className="mb-3">
							<label className="form-label">{t("Email")}</label>
							<Input
								type="email"
								value={currentSupplier.email}
								onChange={(e) =>
									setCurrentSupplier((prev) => ({ ...prev, email: e.target.value }))
								}
								placeholder={t("Enter email address")}
							/>
						</div>
					</Col>
					<Col md={6}>
						<div className="mb-3">
							<label className="form-label">{t("Phone")}</label>
							<PhoneInput
								country="in"
								value={`${currentSupplier.phoneCode || ""}${currentSupplier.phone || ""}`}
								onChange={(value, phoneCountry) => {
									setCurrentSupplier((prev) => ({
										...prev,
										phoneCode: `+${phoneCountry.dialCode}`,
										phone: value.replace(phoneCountry.dialCode, ""),
									}));
								}}
								inputClass="form-control"
								containerClass="form-control p-0"
							/>
						</div>
					</Col>
				</Row>

				<div className="mb-3">
					<label className="form-label">{t("Address")}</label>
					<Input
						type="textarea"
						value={currentSupplier.address}
						onChange={(e) =>
							setCurrentSupplier((prev) => ({ ...prev, address: e.target.value }))
						}
						placeholder={t("Enter complete address")}
						rows="2"
					/>
				</div>

				<Row>
					<Col md={6}>
						<div className="mb-3">
							<label className="form-label">{t("Country")}</label>
							<CountrySelect
								value={country}
								onChange={handleCountryChange}
								placeholder={t("Search country...")}
								inModal
							/>
						</div>
					</Col>
					<Col md={6}>
						<div className="mb-3">
							<label className="form-label">{t("State")}</label>
							<StateSelect
								options={states}
								value={state}
								onChange={handleStateChange}
								placeholder={
									country ? t("Select state") : t("Select country first")
								}
								isDisabled={!country}
								isLoading={statesLoading}
								inModal
							/>
						</div>
					</Col>
				</Row>

				<Row>
					{showStateCode && (
						<Col md={6}>
							<div className="mb-3">
								<label className="form-label">{t("State Code")}</label>
								<Input
									type="text"
									value={currentSupplier.stateCode || ""}
									readOnly
									placeholder={t("Auto-filled from state selection")}
								/>
							</div>
						</Col>
					)}
					<Col md={showStateCode ? 6 : 12}>
						<div className="mb-3">
							<label className="form-label">{taxNumberLabel}</label>
							<Input
								type="text"
								value={currentSupplier.gstNumber}
								onChange={(e) =>
									setCurrentSupplier((prev) => ({
										...prev,
										gstNumber: e.target.value.toUpperCase(),
									}))
								}
								placeholder={taxNumberPlaceholder}
								style={{ textTransform: "uppercase" }}
							/>
						</div>
					</Col>
				</Row>
			</Modal.Body>
			<Modal.Footer>
				<Button color="primary" onClick={handleSaveSupplier}>
					{isEdit ? t("Update") : t("Submit")}
				</Button>
				<Button color="secondary" onClick={handleOnHide}>
					{t("Cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default SupplierModal;
